import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import useVendorCurrency from "../../../hooks/useVendorCurrency";
import utilFunctions from "../../../utilities/utilFunctions";

var moment = require("moment");

const WasteLogDateGroup = ({ date, logs, onEdit, onDelete }) => {
  const { locale, currency, currencySymbol } = useVendorCurrency();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);

  const handleMenuOpen = (event, log) => {
    setAnchorEl(event.currentTarget);
    setSelectedLog(log);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedLog(null);
  };

  const handleEdit = () => {
    if (selectedLog && onEdit) {
      onEdit(selectedLog);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedLog && onDelete) {
      onDelete(selectedLog);
    }
    handleMenuClose();
  };

  const formatDate = (dateString) => {
    return moment(dateString).format("Do MMMM YYYY");
  };

  const formatCurrency = (amountInCents) => {
    if (amountInCents === null || amountInCents === undefined) {
      return `${currencySymbol}0.00`;
    }
    const formatted = utilFunctions.convertCentsToLargerFormatCurrency(
      amountInCents,
      locale,
      currency
    );
    return `${currencySymbol}${formatted}`;
  };

  return (
    <Box sx={{ marginBottom: 4 }}>
      {/* Date Header */}
      <Typography
        variant="h6"
        sx={{
          marginBottom: 2,
          fontWeight: 600,
          fontSize: { xs: 16, md: 18 },
        }}
      >
        {formatDate(date)}
      </Typography>

      {/* Table */}
      <TableContainer
        sx={{
          backgroundColor: (theme) => theme.palette.pure.white,
          borderRadius: 2,
          border: "1px solid",
          borderColor: (theme) => theme.palette.terrain[300],
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: (theme) => theme.palette.terrain[300],
              }}
            >
              <TableCell sx={{ fontWeight: 600, width: "80%", paddingY: 1.5 }}>
                Product Name
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: "15%", paddingY: 1.5 }}>
                Value Wasted
              </TableCell>
              <TableCell sx={{ width: "5%", paddingY: 1.5 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow
                key={log.id}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  "&:hover": {
                    backgroundColor: (theme) => theme.palette.terrain[50],
                  },
                }}
              >
                <TableCell>
                  <Typography variant="body1" fontWeight={500}>
                    {log.productName || "Unknown Product"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: (theme) => theme.palette.primary.main }}
                  >
                    {log.formatName || ""}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">
                    {formatCurrency(log.wastedValue?.amount)}
                  </Typography>
                  {log.quantity && (
                    <Typography variant="body2" color="text.secondary">
                      {log.quantity.amount}{" "}
                      {log.quantity.unitOfMeasurement?.abbreviation ||
                        log.quantity.unitOfMeasurement?.name ||
                        "units"}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, log)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default WasteLogDateGroup;
